<template>
  <div class="sp-tooltip">
    <div
      ref="activator"
      class="sp-tooltip__activator"
      :tabindex="activatorTabindex"
      :aria-describedby="tooltipId"
      :aria-expanded="isVisible"
      aria-haspopup="true"
      :aria-disabled="isDisabled"
      @mouseenter="onTriggerEnter"
      @mouseleave="onTriggerLeave"
      @focusin="onTriggerEnter"
      @focusout="closeImmediate"
      @click="toggle"
    >
      <slot />
    </div>

    <transition name="fade" :css="fadesOut" @before-leave="rememberFadingPopup" @after-leave="forgetFadingPopup">
      <div
        v-if="isVisible"
        :id="tooltipId"
        ref="popup"
        class="sp-tooltip__popup"
        :class="popupClassModifiers"
        role="tooltip"
        popover="manual"
        @mouseenter="onTooltipEnter"
        @mouseleave="onTooltipLeave"
      >
        <div class="sp-tooltip__popup-content-wrapper">
          <div class="sp-tooltip__popup-content">
            <slot name="content">{{ content }}</slot>
          </div>
          <div class="sp-tooltip__popup-arrow" :class="popupArrowClassModifiers"></div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { onKeyStroke, useDebounceFn, useEventListener, useWindowSize } from "@vueuse/core";
import { computed, nextTick, onBeforeUnmount, ref, useTemplateRef, watch } from "vue";
import { randomUUID } from "#/shared/utils/string";
import { isPresent, toBoolean, toNumber } from "#ce/utils/props";
import { useExclusiveTooltip } from "./composables/useExclusiveTooltip.js";
import { props as tooltipProps } from "./props.js";

/**
 * Component Props
 *
 * @property {string|undefined} content - Tooltip content as string (if no content slot is used)
 * @property {"top"|"bottom"|"left"|"right"} position - Preferred tooltip position
 * @property {boolean|string} disabled - Whether the tooltip is disabled
 * @property {number|string} maxViewportWidth - Widest viewport in px at which the tooltip opens (0: any width)
 * @property {boolean|string} noWrap - Whether the tooltip text should wrap or be ellipsized
 * @property {number|string} openDelay - Delay in ms before showing tooltip (default: 0)
 * @property {number|string} closeDelay - Delay in ms before hiding tooltip (default: 200)
 */
const props = defineProps(tooltipProps);

const isVisible = ref(false);
const activator = useTemplateRef("activator");
const tooltipId = `tooltip-${randomUUID()}`;
const isDisabled = computed(() => isPresent(props.disabled));
const activatorTabindex = computed(() => (toBoolean(props.noActivatorFocus) ? undefined : 0));
const popup = useTemplateRef("popup");

// Tooltip open/close delay timers
const openTimerId = ref(null);
const closeTimerId = ref(null);
const isOverTrigger = ref(false);
const isOverTooltip = ref(false);

function clearOpenTimer() {
  if (openTimerId.value) {
    window.clearTimeout(openTimerId.value);
    openTimerId.value = null;
  }
}

function clearCloseTimer() {
  if (closeTimerId.value) {
    window.clearTimeout(closeTimerId.value);
    closeTimerId.value = null;
  }
}

// Viewport dimensions
const { width: vpWidth, height: vpHeight } = useWindowSize();

const isAboveMaxViewportWidth = computed(() => {
  const max = toNumber(props.maxViewportWidth, 0);
  return max > 0 && vpWidth.value > max;
});
const isSuppressed = computed(() => isDisabled.value || isAboveMaxViewportWidth.value);

// Tracks the current placement of the tooltip
const currentPosition = ref(props.position);

/**
 * Show tooltip on mouseenter or focus
 */
function onTriggerEnter() {
  if (isSuppressed.value) {
    return;
  }
  isOverTrigger.value = true;
  scheduleOpen();
}

/**
 * Hide tooltip on mouseleave or blur (with delay)
 */
function onTriggerLeave() {
  isOverTrigger.value = false;
  if (!isOverTooltip.value) {
    scheduleClose();
  }
}

/**
 * Keep tooltip open if hovered directly
 */
function onTooltipEnter() {
  isOverTooltip.value = true;
  clearCloseTimer();
}

function onTooltipLeave() {
  isOverTooltip.value = false;
  if (!isOverTrigger.value) {
    scheduleClose();
  }
}

/**
 * Toggle tooltip visibility
 */
function toggle() {
  if (isSuppressed.value) {
    return;
  }
  clearOpenTimer();
  clearCloseTimer();
  isVisible.value = !isVisible.value;
  schedulePositionUpdate();
}

/**
 * Show the tooltip and schedule a position update
 */
function open() {
  clearCloseTimer();
  isVisible.value = true;
  schedulePositionUpdate();
}

/**
 * Hide the tooltip
 */
function close() {
  clearOpenTimer();
  isVisible.value = false;
}

function closeImmediate() {
  clearOpenTimer();
  clearCloseTimer();
  isVisible.value = false;
}

function scheduleOpen() {
  clearCloseTimer();
  clearOpenTimer();

  const delay = toNumber(props.openDelay, 0);
  if (delay <= 0) {
    open();
    return;
  }

  openTimerId.value = window.setTimeout(() => {
    openTimerId.value = null;
    if (isOverTrigger.value && !isSuppressed.value) {
      open();
    }
  }, delay);
}

function scheduleClose() {
  clearOpenTimer();
  clearCloseTimer();

  const delay = toNumber(props.closeDelay, 200);
  if (delay <= 0) {
    close();
    return;
  }

  closeTimerId.value = window.setTimeout(() => {
    closeTimerId.value = null;
    if (!isOverTrigger.value && !isOverTooltip.value) {
      close();
    }
  }, delay);
}

/**
 * Recalculate and apply the tooltip position relative to the activator
 */
function updatePosition() {
  // If the tooltip is not visible, don't update the position
  if (!isVisible.value) {
    return;
  }

  const rect = activator.value?.getBoundingClientRect();
  if (!rect) {
    return;
  }

  requestAnimationFrame(() => updateTooltipPopup(rect));
}

/**
 * Handles the full tooltip popup update
 */
function updateTooltipPopup(rect) {
  const popupEl = popup.value;
  if (!popupEl) {
    return;
  }

  showInTopLayer(popupEl);

  const popupRect = popupEl.getBoundingClientRect();
  const spacing = toNumber(props.popupSpacing, 12);

  updateCurrentPosition(rect, popupRect, spacing);
  applyPopupStyles(popupEl, rect, popupRect, spacing);
}

/**
 * Show the popup in the browser's top layer, above every stacking context on the page, so no
 * ancestor's z-index or overflow can cover or clip it. Browsers without the Popover API keep the
 * fixed-position popup in place.
 */
function showInTopLayer(popupEl) {
  if (popupEl.showPopover && !popupEl.matches(":popover-open")) {
    popupEl.showPopover();
  }
}

/**
 * Decide tooltip position and update `currentPosition`
 */
function updateCurrentPosition(rect, popupRect, spacing) {
  // Space available around activator
  const spaceAbove = rect.top;
  const spaceBelow = vpHeight.value - rect.bottom;
  const spaceLeft = rect.left;
  const spaceRight = vpWidth.value - rect.right;

  currentPosition.value = props.position;

  // Determine fallback position if preferred doesn't fit
  const fits = {
    top: spaceAbove >= popupRect.height + spacing,
    bottom: spaceBelow >= popupRect.height + spacing,
    left: spaceLeft >= popupRect.width + spacing,
    right: spaceRight >= popupRect.width + spacing,
  };

  if (!fits[props.position]) {
    const fallbackOrder = ["bottom", "top", "right", "left"];
    const fallback = fallbackOrder.find((pos) => fits[pos]);

    if (fallback) {
      currentPosition.value = fallback;
    } else {
      currentPosition.value = "bottom";
    }
  }
}

/**
 * Apply calculated top/left/transform values to the popup
 */
function applyPopupStyles(el, rect, popupRect, spacing) {
  const rawPosition = calculateRawPosition(currentPosition.value, { rect, popupRect, spacing });
  let { top, left, transform } = rawPosition;

  if (["top", "bottom"].includes(currentPosition.value)) {
    const overflowRight = left + popupRect.width / 2 - vpWidth.value;
    const overflowLeft = popupRect.width / 2 - left;

    // Horizontal overflow correction for top/bottom tooltips
    if (overflowRight > 0) {
      left = vpWidth.value - popupRect.width - spacing;
      transform = "none";
    } else if (overflowLeft > 0) {
      left = spacing;
      transform = "none";
    }
  }

  // Vertical overflow correction for left/right tooltips
  if (["left", "right"].includes(currentPosition.value)) {
    const overflowTop = top - popupRect.height / 2;
    const overflowBottom = top + popupRect.height / 2 - vpHeight.value;

    if (overflowTop < 0) {
      top = spacing;
      transform = "none";
    } else if (overflowBottom > 0) {
      top = vpHeight.value - popupRect.height - spacing;
      transform = "none";
    }
  }

  Object.assign(el.style, {
    position: "fixed",
    top: `${top}px`,
    left: `${left}px`,
    visibility: "visible",
    transform,
  });
}

function calculateRawPosition(currentPosition, { rect, popupRect, spacing }) {
  // Position strategies per direction
  let transform = "translateX(-50%)";
  const left = rect.left + rect.width / 2;

  if (currentPosition === "top") {
    return {
      top: rect.top - popupRect.height - spacing,
      left,
      transform,
    };
  }

  if (currentPosition === "bottom") {
    return {
      top: rect.bottom + spacing,
      left,
      transform,
    };
  }

  transform = "translateY(-50%)";
  const top = rect.top + rect.height / 2;

  if (currentPosition === "left") {
    return {
      top,
      left: rect.left - popupRect.width - spacing,
      transform,
    };
  }

  return {
    top,
    left: rect.right + spacing,
    transform,
  };
}

/**
 * Schedule updatePosition with nextTick and animation frame for best stability
 */
async function schedulePositionUpdate() {
  await nextTick();
  requestAnimationFrame(updatePosition);
}

/**
 * Close a visible tooltip; bound to window resize (debounced) and Escape.
 */
function closeIfVisible() {
  if (!isVisible.value) {
    return;
  }

  // The popup is removed without a mouseleave, so the hover state must reset here.
  isOverTrigger.value = false;
  isOverTooltip.value = false;
  closeImmediate();
}

function closeWhenSuppressed(suppressed) {
  if (suppressed) {
    closeIfVisible();
  }
}

watch(isSuppressed, closeWhenSuppressed);

const { fadesOut, rememberFadingPopup, forgetFadingPopup } = useExclusiveTooltip({
  tooltipId,
  isVisible,
  close: closeIfVisible,
});

const debounceDelay = 100;
const debouncedCloseIfVisible = useDebounceFn(closeIfVisible, debounceDelay);

function closeOnScroll() {
  isOverTrigger.value = false;
  isOverTooltip.value = false;
  closeImmediate();
}

// Event bindings
useEventListener(window, "resize", debouncedCloseIfVisible);
useEventListener(window, "scroll", closeOnScroll, { capture: true, passive: true });
useEventListener(window, "wheel", closeOnScroll, { passive: true });
useEventListener(window, "touchmove", closeOnScroll, { passive: true });
onKeyStroke("Escape", closeIfVisible);

// Cleanup timers on component unmount
onBeforeUnmount(() => {
  clearOpenTimer();
  clearCloseTimer();
});

/**
 * Computed classes for the popup
 */
const popupClassModifiers = computed(() => ({
  "--no-wrap": toBoolean(props.noWrap),
  [`--theme-${props.theme}`]: true,
}));

/**
 * Computed classes for the directional arrow
 */
const popupArrowClassModifiers = computed(() => ({
  [`--popup-position-${currentPosition.value}`]: true,
}));
</script>

<style lang="scss">
:host {
  --background-color: var(--sp-comp-tooltip-background, var(--sp-sys-color-surface, white));
  --text-color: var(--sp-comp-tooltip-text-color, var(--sp-sys-color-on-surface, black));
  --box-shadow: var(--sp-comp-tooltip-shadow, var(--sp-sys-elevation-1, 0 1px 3px rgba(0, 0, 0, 0.2)));
  --shadow-filter: var(--sp-comp-tooltip-shadow-filter, drop-shadow(0 1px 3px rgba(0, 0, 0, 0.2)));
  --padding: var(--sp-comp-tooltip-padding, 0.75rem 1rem);
  --font: var(--sp-comp-tooltip-font, var(--sp-sys-typescale-small-medium-short, inherit));
  --popup-border-width: var(--sp-comp-tooltip-border-width, 0px);
  --popup-border-style: var(--sp-comp-tooltip-border-style, solid);
  --popup-border-color: var(--sp-comp-tooltip-border-color, transparent);
  --popup-border: var(--popup-border-width) var(--popup-border-style) var(--popup-border-color);
  --popup-border-radius: var(--sp-comp-tooltip-border-radius, 0.5rem);
  --popup-min-height: var(--sp-comp-tooltip-min-height, auto);
  --popup-min-width: var(--sp-comp-tooltip-min-width, auto);

  /* Light theme */
  --light-background-color: var(--sp-comp-tooltip-light-background, var(--sp-sys-color-surface, white));
  --light-text-color: var(--sp-comp-tooltip-light-text-color, var(--sp-sys-color-on-surface, black));
  --light-shadow: var(--sp-comp-tooltip-light-shadow, var(--sp-sys-elevation-1));
  --light-shadow-filter: var(--sp-comp-tooltip-light-shadow-filter, var(--sp-sys-shadow-filter-1));
  --light-padding: var(--sp-comp-tooltip-light-padding, 0.75rem 1rem);
  --light-font: var(--sp-comp-tooltip-light-font, var(--sp-sys-typescale-small-medium-short, inherit));
  --light-font-size: var(--sp-comp-tooltip-light-font-size, var(--sp-sys-typescale-small-font-size));
  --light-border-radius: var(--sp-comp-tooltip-light-border-radius, var(--sp-ref-border-radius-sm));
  --light-border-width: var(--sp-comp-tooltip-light-border-width, var(--sp-sys-border-width-default));
  --light-border-style: var(--sp-comp-tooltip-light-border-style, solid);
  --light-border-color: var(--sp-comp-tooltip-light-border-color, transparent);
  --light-min-height: var(--sp-comp-tooltip-light-min-height, auto);
  --light-min-width: var(--sp-comp-tooltip-light-min-width, auto);

  /* Dark theme */
  --dark-background-color: var(--sp-comp-tooltip-dark-background, var(--sp-sys-color-on-surface, black));
  --dark-text-color: var(--sp-comp-tooltip-dark-text-color, var(--sp-sys-color-surface, white));
  --dark-shadow: var(--sp-comp-tooltip-dark-shadow, var(--sp-sys-elevation-1));
  --dark-shadow-filter: var(--sp-comp-tooltip-dark-shadow-filter, var(--sp-sys-shadow-filter-1));
  --dark-padding: var(--sp-comp-tooltip-dark-padding, 0.75rem 1rem);
  --dark-font: var(--sp-comp-tooltip-dark-font, var(--sp-sys-typescale-small-medium-short, inherit));
  --dark-font-size: var(--sp-comp-tooltip-dark-font-size, var(--sp-sys-typescale-small-font-size));
  --dark-border-radius: var(--sp-comp-tooltip-dark-border-radius, var(--sp-ref-border-radius-sm));
  --dark-border-width: var(--sp-comp-tooltip-dark-border-width, var(--sp-sys-border-width-default));
  --dark-border-style: var(--sp-comp-tooltip-dark-border-style, solid);
  --dark-border-color: var(--sp-comp-tooltip-dark-border-color, transparent);
  --dark-border: var(--popup-border-width) var(--popup-border-style) var(--dark-border-color);
  --dark-min-height: var(--sp-comp-tooltip-dark-min-height, auto);
  --dark-min-width: var(--sp-comp-tooltip-dark-min-width, auto);

  display: inline-block;
}
</style>

<style lang="scss" scoped>
.sp-tooltip__activator {
  cursor: v-bind(activatorCursor);
}

/**
  * Tooltip wrapper popup with dynamic positioning. <div> element is used to allow focus management and accessibility.
 */
.sp-tooltip__popup {
  width: var(--sp-comp-tooltip-width, auto);
  max-width: var(--sp-comp-tooltip-max-width, 18rem);
  background: transparent;
  display: block;
  box-sizing: border-box;
  position: fixed;
  visibility: hidden; // Hidden by default, shown when open
  opacity: 1;
  transition: none;
  z-index: var(--sp-comp-tooltip-z-index, var(--sp-ref-z-index-max));
  // Undo the browser's popover box (centred with inset and margin, bordered, padded, scrolling).
  inset: auto;
  margin: 0;
  padding: 0;
  border: 0;
  overflow: visible;
  color: inherit;

  @each $theme in light, dark {
    &.--theme-#{$theme} {
      --background-color: var(--#{$theme}-background-color);
      --text-color: var(--#{$theme}-text-color);
      --box-shadow: var(--#{$theme}-shadow);
      --shadow-filter: var(--#{$theme}-shadow-filter);
      --padding: var(--#{$theme}-padding);
      --font: var(--#{$theme}-font);
      --font-size: var(--#{$theme}-font-size);
      --popup-border-radius: var(--#{$theme}-border-radius);
      --popup-border-width: var(--#{$theme}-border-width);
      --popup-border-style: var(--#{$theme}-border-style);
      --popup-border-color: var(--#{$theme}-border-color);
      --popup-min-height: var(--#{$theme}-min-height);
      --popup-min-width: var(--#{$theme}-min-width);
    }
  }

  &.--theme-dark {
    --popup-border: var(--dark-border);
  }

  /**
  * Disables wrapping inside tooltip (used when content should stay on a single line).
  */
  &.--no-wrap {
    white-space: nowrap;
    width: max-content;
    min-width: var(--sp-comp-tooltip-no-wrap-min-width, 3rem);
    max-width: var(--sp-comp-tooltip-no-wrap-max-width, 100%);
    text-overflow: ellipsis;
    overflow: hidden;
  }

  /**
  * Fade transition for tooltip appearance and disappearance.
  */
  &.fade-enter-active,
  &.fade-leave-active {
    transition: opacity 0.2s ease-in-out;
  }

  &.fade-enter-from,
  &.fade-leave-to {
    opacity: 0;
  }

  .sp-tooltip__popup-content-wrapper {
    box-sizing: border-box;
    box-shadow: none;
    filter: var(--shadow-filter);
    transform: translateZ(0);
    will-change: filter;
    border: var(--popup-border);
    border-radius: var(--popup-border-radius);
    background-color: var(--background-color);
    min-width: var(--popup-min-width);
  }

  /**
  * Main tooltip content container.
  */
  .sp-tooltip__popup-content {
    display: flex;
    align-items: center;
    min-height: var(--popup-min-height);
    border-radius: var(--popup-border-radius);
    color: var(--text-color);
    font: var(--font);
    font-size: var(--font-size, inherit);
    box-sizing: border-box;
    text-align: var(--sp-comp-tooltip-text-align, center);
    justify-content: var(--sp-comp-tooltip-justify-content, flex-start);
    position: relative;
    padding: var(--padding);
    background: var(--background-color);
    z-index: 2;
  }

  .sp-tooltip__popup-arrow {
    --popup-pointer-size: var(--sp-comp-tooltip-pointer-size, 0.75rem);
    --pointer-position: calc(
      50% - (var(--popup-pointer-size) / 2) - var(--popup-border-width)
    ); // default is top center (popup position bottom)
    --pointer-size: var(--popup-pointer-size);

    position: absolute;
    top: 0;
    left: var(--pointer-position);
    width: 100%;
    z-index: 1;
    box-sizing: border-box;
    pointer-events: none;

    &::before {
      background-color: var(--background-color);
      border: var(--popup-border);
      box-shadow: none;
      content: "";
      position: absolute;
      left: 0;
      height: var(--pointer-size);
      width: var(--pointer-size);
      transform: rotate(-45deg);
      top: calc(var(--pointer-size) / 2 * -1);
      z-index: -1;
    }

    &.--popup-position-top {
      top: calc(100% - (var(--popup-border-width) * 2));
    }

    &.--popup-position-right,
    &.--popup-position-left {
      top: calc(50% - var(--popup-border-width));
    }

    &.--popup-position-left {
      left: calc(100% - (var(--popup-pointer-size)) / 2 - (var(--popup-border-width) * 2));
    }

    &.--popup-position-right {
      left: calc(var(--pointer-size) * -1 / 2);
    }
  }
}
</style>
